import { areaOfCircle, radiusOfCircle } from '../../geometryUtil';
import { NumericRange } from '../types';
import { denormalize, normalize, range } from './util';

export const xScaler =
  (pixels: NumericRange, domain: NumericRange, zoom: NumericRange = [0, 1]) =>
  (value: number): number => {
    const normalizedValue = normalize(domain, value);
    const zoomRange = range(zoom);
    const pixelRange = range(pixels);

    return pixels[0] + (pixelRange * (normalizedValue - zoom[0])) / zoomRange;
  };
export const xUnscaler =
  (pixels: NumericRange, domain: NumericRange, zoom: NumericRange) =>
  (value: number): number => {
    const normalizedValue = normalize(pixels, value);
    const normalizedAndZoomed = zoom[0] + range(zoom) * normalizedValue;
    return domain[0] + normalizedAndZoomed * range(domain);
  };

export const yScaler =
  (pixels: NumericRange, domain: NumericRange, zoom: NumericRange = [0, 1]) =>
  (value: number): number => {
    const normalizedValue = normalize(domain, value);
    const zoomRange = range(zoom);
    const pixelRange = range(pixels);
    return (
      pixels[0] + (pixelRange * (1 - normalizedValue - zoom[0])) / zoomRange
    );
  };

export const yUnscaler =
  (pixels: NumericRange, domain: NumericRange, zoom: NumericRange) =>
  (value: number): number => {
    const normalizedValue = normalize(pixels, value);
    const normalizedAndZoomed = zoom[0] + range(zoom) * normalizedValue;
    return domain[0] + (1 - normalizedAndZoomed) * range(domain);
  };

const getRadiusScalerParameters = (
  radiusRange: NumericRange,
  scale: number
) => {
  const minRadius = radiusRange[0] * (1 / scale);
  const maxRadius = radiusRange[1] * (1 / scale);

  const minArea = areaOfCircle(minRadius);
  const maxArea = areaOfCircle(maxRadius);
  const areaRange = range([minArea, maxArea]);

  return { minArea, areaRange };
};

export const radiusScaler =
  (radiusRange: NumericRange, domain: NumericRange, scale = 1) =>
  (value: number): number => {
    const normalizedValue = normalize(domain, value);
    const { minArea, areaRange } = getRadiusScalerParameters(
      radiusRange,
      scale
    );

    const area = minArea + areaRange * normalizedValue;
    const radius = radiusOfCircle(area);
    return radius;
  };

export const radiusUnscaler =
  (radiusRange: NumericRange, domain: NumericRange, scale = 1) =>
  (radius: number) => {
    const { minArea, areaRange } = getRadiusScalerParameters(
      radiusRange,
      scale
    );
    const area = areaOfCircle(radius);
    const normalizedValue = areaRange ? (area - minArea) / areaRange : 0;
    const dataValue = denormalize(domain, normalizedValue);
    return dataValue;
  };
